import React, { useEffect, useMemo, useState } from "react";
import StandardModal, { StandardModalBody, StandardModalFooter, useModalLoading } from '../../../components/modales/StandardModal';
import { Alert, Box, Button, Checkbox, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { LoadingButton } from '@routes-plan/ui';
import axios from "axios";
import { useT } from "../../../i18n";
import { formatCurrency as companyMoney } from '../../../utils/format/currency';

export interface MyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onButtonClick: () => void;
  id: string;
  showAlert: (message: string, severity?: "success" | "info" | "warning" | "error") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type BankOption = {
  id: number;
  name: string;
};

type RouteDiscountSettings = {
  discountDays?: number | string;
  allowManualDiscount?: boolean | number | string;
};

type PaymentDetail = {
  id: number;
  value: number | string;
  discountValue: number | string;
  balance: number | string;
  cash: boolean;
  bankReceipt?: string | null;
  bank?: {
    id: number;
    name: string;
  } | null;
  loan?: {
    id: number;
    balance: number | string;
    installmentValue: number | string;
    date?: string | null;
    interestValue?: number | string;
    route?: RouteDiscountSettings | null;
    client?: {
      fullName: string;
      idNumber: string;
    } | null;
  } | null;
};

const parseAmount = (value: string | number | null | undefined) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalizedValue = String(value ?? "").trim().replace(",", ".");
  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const normalizeDiscountDays = (value: unknown) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : 0;
};

const getLoanAgeDays = (loanDateValue: unknown) => {
  if (!loanDateValue) return null;

  const parsedDate = new Date(String(loanDateValue));
  if (Number.isNaN(parsedDate.getTime())) return null;

  const today = new Date();
  const utcStart = Date.UTC(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
  );
  const utcToday = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  return Math.max(0, Math.floor((utcToday - utcStart) / 86400000));
};

const getCoordinates = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve(`${position.coords.latitude}, ${position.coords.longitude}`);
      },
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  });
};

const EditPaymentModal: React.FC<MyModalProps> = ({
  isOpen,
  onClose,
  onButtonClick,
  id,
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const t = useT();
  // El Cargando general queda detrás del modal: se usa el del modal estándar.
  const { loading: modalBusy, showLoading: showModalLoading, hideLoading: hideModalLoading } = useModalLoading();
  const [payDetail, setPayDetail] = useState<PaymentDetail | null>(null);
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [userId, setUserId] = useState<string>("");
  const [location, setLocation] = useState<string | null>(null);
  const [paymentValue, setPaymentValue] = useState("0");
  const [discountValue, setDiscountValue] = useState("0");
  const [selectedFormPay, setSelectedFormPay] = useState("1");
  const [selectedBank, setSelectedBank] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [discountEnabled, setDiscountEnabled] = useState(false);

  const currencyFormatter = useMemo(() => ({ format: (v: number) => companyMoney(v) }), []);

  const originalValue = parseAmount(payDetail?.value);
  const originalDiscount = parseAmount(payDetail?.discountValue);
  const currentLoanBalance = parseAmount(payDetail?.loan?.balance);
  const baseBalance = currentLoanBalance + originalValue + originalDiscount;
  const routeSettings = payDetail?.loan?.route ?? null;
  const discountDays = normalizeDiscountDays(routeSettings?.discountDays);
  const enableDiscountManual = Boolean(
    Number(routeSettings?.allowManualDiscount ?? 1),
  );
  const loanAgeDays = getLoanAgeDays(payDetail?.loan?.date);
  const utilityValue = Math.max(0, parseAmount(payDetail?.loan?.interestValue));
  const canDiscount =
    loanAgeDays !== null &&
    loanAgeDays <= discountDays &&
    utilityValue > 0;
  const discountAmount = discountEnabled ? Math.max(0, utilityValue * 0.5) : 0;
  const newValue = parseAmount(paymentValue);
  const manualDiscountRaw = enableDiscountManual
    ? Math.min(Math.max(0, parseAmount(discountValue)), baseBalance)
    : 0;
  const appliedDiscount = enableDiscountManual
    ? Math.min(manualDiscountRaw, Math.max(0, baseBalance - newValue))
    : discountEnabled && canDiscount
      ? discountAmount
      : 0;
  const newTotal = newValue + appliedDiscount;
  const projectedBalance = Math.max(0, baseBalance - newTotal);
  const balanceDifference = projectedBalance - currentLoanBalance;
  const maxAbono = Math.max(0, baseBalance - discountAmount);

  const resetState = () => {
    setPayDetail(null);
    setPaymentValue("0");
    setDiscountValue("0");
    setSelectedFormPay("1");
    setSelectedBank("");
    setReceiptNumber("");
    setErrorMessage("");
    setIsSaving(false);
    setDiscountEnabled(false);
  };

  useEffect(() => {
    if (!isOpen || !id) {
      return;
    }

    let isMounted = true;

    const loadData = async () => {
      try {
        showModalLoading();
        const [payResponse, banksResponse, userResponse] = await Promise.all([
          axios.get(`payments/${id}`),
          axios.get("/banks"),
          axios.get("user"),
        ]);

        if (!isMounted) {
          return;
        }

        const nextPay: PaymentDetail = payResponse.data;
        const nextEnableDiscountManual = Boolean(
          Number(nextPay.loan?.route?.allowManualDiscount ?? 1),
        );
        const nextDiscountValue = String(parseAmount(nextPay.discountValue));

        setPayDetail(nextPay);
        setBanks(banksResponse.data?.data ?? []);
        setUserId(String(userResponse.data?.data?.id ?? ""));
        setPaymentValue(String(parseAmount(nextPay.value)));
        setDiscountValue(nextDiscountValue);
        setSelectedFormPay(nextPay.cash ? "1" : "0");
        setSelectedBank(nextPay.bank?.id ? String(nextPay.bank.id) : "");
        setReceiptNumber(nextPay.bankReceipt ?? "");
        setDiscountEnabled(!nextEnableDiscountManual && parseAmount(nextPay.discountValue) > 0);
        setErrorMessage("");
      } catch (error) {
        console.error("Error loading pay data:", error);
        showAlert(t("paymentCorrections.loadError"), "error");
      } finally {
        hideModalLoading();
      }

      try {
        const coords = await getCoordinates();
        if (isMounted) {
          setLocation(coords);
        }
      } catch (error) {
        if (isMounted) {
          setLocation(null);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id, isOpen, hideModalLoading, showAlert, showModalLoading, t]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!canDiscount && discountEnabled && !enableDiscountManual) {
      setDiscountEnabled(false);
      setDiscountValue("0");
    }
  }, [canDiscount, discountEnabled, enableDiscountManual]);

  useEffect(() => {
    if (enableDiscountManual) {
      return;
    }

    if (discountEnabled && canDiscount) {
      setPaymentValue(String(maxAbono));
      setDiscountValue(String(discountAmount));
    }
  }, [canDiscount, discountAmount, discountEnabled, enableDiscountManual, maxAbono]);

  useEffect(() => {
    if (!payDetail) {
      return;
    }

    if (newTotal <= 0) {
      setErrorMessage(t("paymentCorrections.totalMustBePositive"));
      return;
    }

    if (newTotal > baseBalance) {
      setErrorMessage(t("paymentCorrections.totalExceedsBalance"));
      return;
    }

    setErrorMessage("");
  }, [baseBalance, newTotal, payDetail, t]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handlePaymentValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    const abono = parseAmount(nextValue);
    const discountForLimit = enableDiscountManual
      ? Math.min(Math.max(0, parseAmount(discountValue)), baseBalance)
      : discountEnabled && canDiscount
        ? discountAmount
        : 0;
    const maxValue = Math.max(0, baseBalance - discountForLimit);

    if (abono > maxValue) {
      setErrorMessage(
        enableDiscountManual
          ? t("paymentCorrections.totalExceedsBalance")
          : t("paymentCorrections.paymentExceedsBalance"),
      );
      setPaymentValue(String(maxValue));
      return;
    }

    setErrorMessage("");
    setPaymentValue(nextValue);
  };

  const handleDiscountValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!enableDiscountManual) {
      return;
    }

    let nextDiscount = Math.min(
      Math.max(0, parseAmount(event.target.value)),
      baseBalance,
    );
    const abono = parseAmount(paymentValue);

    if (abono + nextDiscount > baseBalance) {
      nextDiscount = Math.max(0, baseBalance - abono);
      setErrorMessage(t("paymentCorrections.totalExceedsBalance"));
    } else {
      setErrorMessage("");
    }

    setDiscountValue(String(nextDiscount));
  };

  const handleFormPayChange = (event: SelectChangeEvent<string>) => {
    setSelectedFormPay(event.target.value);
    if (event.target.value === "1") {
      setSelectedBank("");
      setReceiptNumber("");
    }
  };

  const getBalanceImpactLabel = () => {
    if (balanceDifference < 0) {
      return t("paymentCorrections.balanceDecreases", { value: currencyFormatter.format(Math.abs(balanceDifference)) });
    }

    if (balanceDifference > 0) {
      return t("paymentCorrections.balanceIncreases", { value: currencyFormatter.format(Math.abs(balanceDifference)) });
    }

    return t("paymentCorrections.balanceUnchanged");
  };

  const handleSubmit = async () => {
    if (!payDetail) {
      return;
    }

    if (selectedFormPay === "0" && (!selectedBank || !receiptNumber.trim())) {
      setErrorMessage(t("paymentCorrections.bankAndReceiptRequired"));
      return;
    }

    if (newTotal <= 0 || newTotal > baseBalance) {
      return;
    }

    let currentLocation = location;
    if (!currentLocation) {
      try {
        currentLocation = await getCoordinates();
        setLocation(currentLocation);
      } catch (error) {
        currentLocation = null;
        showAlert(t("paymentCorrections.noLocation"), "warning");
      }
    }

    const formData: Record<string, unknown> = {
      value: newValue,
      discountValue: appliedDiscount,
      cash: selectedFormPay === "1",
      bankId: selectedFormPay === "0" ? Number(selectedBank) : null,
      bankReceipt: selectedFormPay === "0" ? receiptNumber.trim() : null,
      location: currentLocation,
      state: true,
    };

    const parsedUserId = Number(userId);
    if (Number.isFinite(parsedUserId) && parsedUserId > 0) {
      formData.userId = parsedUserId;
    }

    try {
      setIsSaving(true);
      showModalLoading();
      const { data } = await axios.put(`/payments/${id}`, formData);
      // Las correcciones esperan la aprobación de otra persona (Préstamos → Aprobar correcciones).
      showAlert(
        data?.code === "CORRECTION_PENDING" ? t("paymentCorrections.pendingApproval") : t("paymentCorrections.updatedOk"),
        data?.code === "CORRECTION_PENDING" ? "info" : "success",
      );
      onButtonClick();
    } catch (error) {
      console.error("Error updating pay:", error);
      showAlert(t("paymentCorrections.updateError"), "error");
    } finally {
      setIsSaving(false);
      hideModalLoading();
    }
  };

  const clientName = payDetail?.loan?.client?.fullName ?? "";
  const clientDocument = payDetail?.loan?.client?.idNumber ?? "";

  return (
    <StandardModal
      open={isOpen}
      onClose={handleClose}
      title={t("paymentCorrections.editTitle")}
      maxWidth={520}
      loading={modalBusy}
      composed
    >
      <StandardModalBody>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Typography variant="subtitle1" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
          {t("paymentCorrections.paymentHeader", { id })} {clientName ? `· ${clientName}` : ""}
        </Typography>
        {clientDocument && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t("paymentCorrections.clientDocument", { value: clientDocument })}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: "action.hover",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(140px, 100%), 1fr))",
                gap: 1.5,
              }}
            >
              {[
                { label: t("paymentCorrections.currentPayment"), value: currencyFormatter.format(originalValue), strong: true },
                { label: t("paymentCorrections.currentDiscount"), value: currencyFormatter.format(originalDiscount) },
                { label: t("paymentCorrections.currentLoanBalance"), value: currencyFormatter.format(currentLoanBalance) },
                { label: t("paymentCorrections.baseBalance"), value: currencyFormatter.format(baseBalance) },
                { label: t("paymentCorrections.projectedBalance"), value: currencyFormatter.format(projectedBalance), strong: true },
              ].map((item) => (
                <Box key={item.label} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {item.label}
                  </Typography>
                  <Typography variant={item.strong ? "subtitle1" : "body2"} sx={{ fontWeight: item.strong ? 600 : 400 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
              <Typography variant="body2" color={balanceDifference === 0 ? "text.secondary" : "primary.main"} sx={{ gridColumn: "1 / -1" }}>
                {getBalanceImpactLabel()}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ gridColumn: "1 / -1" }}>
            <Typography variant="body2" color="text.secondary">
              {t("paymentCorrections.loanDate", { value: payDetail?.loan?.date ?? t("paymentCorrections.notAvailable") })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("paymentCorrections.loanAgeDays", { value: loanAgeDays ?? t("paymentCorrections.notAvailable") })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("paymentCorrections.discountBaseProfit", { value: currencyFormatter.format(utilityValue) })}
            </Typography>
          </Box>

          <Box sx={{ gridColumn: "1 / -1" }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("paymentCorrections.paymentMethod")}</InputLabel>
              <Select value={selectedFormPay} label={t("paymentCorrections.paymentMethod")} onChange={handleFormPayChange}>
                <MenuItem value="1">{t("paymentCorrections.cash")}</MenuItem>
                <MenuItem value="0">{t("paymentCorrections.transfer")}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {selectedFormPay === "0" && (
            <>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("paymentCorrections.bank")}</InputLabel>
                  <Select
                    value={selectedBank}
                    label={t("paymentCorrections.bank")}
                    onChange={(event) => setSelectedBank(event.target.value)}
                  >
                    {banks.map((bank) => (
                      <MenuItem key={bank.id} value={String(bank.id)}>
                        {bank.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ gridColumn: "1 / -1" }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("paymentCorrections.receiptNumber")}
                  value={receiptNumber}
                  onChange={(event) => setReceiptNumber(event.target.value)}
                />
              </Box>
            </>
          )}

          {enableDiscountManual && baseBalance > 0 && (
            <Box sx={{ gridColumn: "1 / -1" }}>
              <TextField
                fullWidth
                size="small"
                label={t("paymentCorrections.discountValue")}
                type="number"
                value={discountValue}
                onChange={handleDiscountValueChange}
                helperText={t("paymentCorrections.manualDiscountHelp")}
              />
            </Box>
          )}

          {canDiscount && !enableDiscountManual && (
            <Box sx={{ gridColumn: "1 / -1" }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={discountEnabled}
                    onChange={(event) => {
                      const nextValue = event.target.checked;
                      setDiscountEnabled(nextValue);
                      if (!nextValue) {
                        setPaymentValue(String(originalValue));
                        setDiscountValue("0");
                      }
                    }}
                  />
                )}
                label={t("paymentCorrections.discount")}
              />
              <Typography variant="body2" color="text.secondary">
                {t("paymentCorrections.calculatedDiscount", { value: currencyFormatter.format(Math.max(0, utilityValue * 0.5)) })}
              </Typography>
            </Box>
          )}

          <Box sx={{ minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              label={t("paymentCorrections.paymentValue")}
              type="number"
              value={paymentValue}
              onChange={handlePaymentValueChange}
              disabled={!enableDiscountManual && discountEnabled}
            />
          </Box>

          {!enableDiscountManual && discountEnabled && (
            <Box sx={{ minWidth: 0 }}>
              <TextField
                fullWidth
                size="small"
                label={t("paymentCorrections.discountValue")}
                type="number"
                value={discountValue}
                InputProps={{ readOnly: true }}
              />
            </Box>
          )}

          <Box sx={{ gridColumn: "1 / -1" }}>
            <TextField
              fullWidth
              size="small"
              label={t("paymentCorrections.currentInstallment")}
              value={currencyFormatter.format(parseAmount(payDetail?.loan?.installmentValue))}
              InputProps={{ readOnly: true }}
            />
          </Box>

          <Box sx={{ gridColumn: "1 / -1" }}>
            <Typography variant="body2" color="text.secondary">
              {t("paymentCorrections.summary", {
                payment: currencyFormatter.format(newValue),
                discount: currencyFormatter.format(appliedDiscount),
                total: currencyFormatter.format(newTotal),
              })}
            </Typography>
          </Box>
        </Box>

      </StandardModalBody>
      <StandardModalFooter>
        <Button variant="outlined" onClick={handleClose}>
          {t("common.cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={isSaving}
          disabled={!payDetail || Boolean(errorMessage)}
        >
          {t("common.save")}
        </LoadingButton>

      </StandardModalFooter>
    </StandardModal>
  );
};

export default EditPaymentModal;
